import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Table, Input, Select, Modal, InputNumber, Tooltip, Empty, Drawer } from 'antd';
import {
  RefreshCw, Sparkles, CreditCard, Users, AlertTriangle, Gift, Activity, Search,
  RotateCcw, XCircle, Copy, CheckCircle2, Clock, ShieldAlert, TrendingUp, ListChecks
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { apiClient } from '../config/apiConfig';
import { useToastMessage } from '../contexts/ToastMessageContext.jsx';

const btn = 'inline-flex !h-8 !min-h-0 items-center gap-1.5 rounded-md !px-3 !py-0 text-xs font-semibold disabled:opacity-50';
const won = (n) => `${Number(n || 0).toLocaleString()}원`;
const fmtDate = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString('ko-KR', { timeZone: 'Asia/Seoul', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' });
};
const errMsg = (error) => error?.response?.data?.message || error?.message || 'error';

const EVENT_TONE = {
  usage_confirm: 'bg-slate-100 text-slate-600',
  quota_exceeded: 'bg-amber-50 text-amber-700',
  pack_purchase: 'bg-emerald-50 text-emerald-700',
  pack_purchase_failed: 'bg-rose-50 text-rose-700',
  admin_grant: 'bg-violet-50 text-violet-700',
  subscription_start: 'bg-sky-50 text-sky-700',
  subscription_renew: 'bg-sky-50 text-sky-700',
  subscription_reactivate: 'bg-sky-50 text-sky-700',
  subscription_cancel: 'bg-slate-100 text-slate-600',
  subscription_renew_failed: 'bg-rose-50 text-rose-700',
  subscription_start_failed: 'bg-rose-50 text-rose-700',
  subscription_billing_error: 'bg-rose-50 text-rose-700',
  credit_grant_failed: 'bg-rose-100 text-rose-800',
};
const EVENT_TYPES = Object.keys(EVENT_TONE);

const ConsultationQuotaMonitorPage = () => {
  const { t: tr } = useTranslation();
  const T = (k, o) => tr(`quotaMonitor.${k}`, o);
  const { showToastMessage } = useToastMessage();
  const toast = (msg, type = 'success') => showToastMessage?.(msg, type);

  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [tab, setTab] = useState('subscribers');
  const [search, setSearch] = useState('');
  const [subFilter, setSubFilter] = useState('active');
  const [eventFilter, setEventFilter] = useState('all');
  const [busyUser, setBusyUser] = useState(null);
  const [renewing, setRenewing] = useState(false);
  const [renewResult, setRenewResult] = useState(null);
  const [grantOpen, setGrantOpen] = useState(false);
  const [grant, setGrant] = useState({ target: '', packs: 1, reason: '' });
  const [granting, setGranting] = useState(false);
  const [detail, setDetail] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await apiClient.get('/api/consultation/admin/quota-stats');
      setData(res.data);
    } catch (error) {
      console.error(error);
      toast(T('msg.loadFailed', { error: errMsg(error) }), 'error');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    setDetail((prev) => (prev ? (data?.subscribers || []).find((s) => s.userId === prev.userId) || null : prev));
  }, [data]);

  const monthKey = data?.monthKey || '—';
  const stats = data?.stats || {};
  const subscribers = data?.subscribers || [];
  const events = data?.recentEvents || [];
  const trend = data?.trend || [];

  const subCounts = useMemo(() => {
    const c = { active: 0, failed: 0, due: 0, canceled: 0, issues: 0, all: subscribers.length };
    subscribers.forEach((s) => {
      if (s.active) c.active += 1;
      if (s.active && s.billingStatus === 'failed') c.failed += 1;
      if (s.active && s.lastBilledMonthKey !== data?.monthKey) c.due += 1;
      if (!s.active) c.canceled += 1;
      if (s.issues?.length) c.issues += 1;
    });
    return c;
  }, [subscribers, data?.monthKey]);

  const filteredSubs = useMemo(() => {
    const q = search.trim().toLowerCase();
    return subscribers.filter((s) => {
      if (subFilter === 'active' && !s.active) return false;
      if (subFilter === 'failed' && !(s.active && s.billingStatus === 'failed')) return false;
      if (subFilter === 'due' && !(s.active && s.lastBilledMonthKey !== data?.monthKey)) return false;
      if (subFilter === 'canceled' && s.active) return false;
      if (subFilter === 'issues' && !s.issues?.length) return false;
      if (!q) return true;
      return [s.userId, s.email, s.name].some((v) => String(v || '').toLowerCase().includes(q));
    });
  }, [subscribers, subFilter, search, data?.monthKey]);

  const filteredEvents = useMemo(() => {
    const q = search.trim().toLowerCase();
    return events.filter((e) => {
      if (eventFilter === 'failures' && !/failed|error/.test(e.type)) return false;
      if (eventFilter !== 'all' && eventFilter !== 'failures' && e.type !== eventFilter) return false;
      if (!q) return true;
      return [e.userId, e.email, e.name, e.type].some((v) => String(v || '').toLowerCase().includes(q));
    });
  }, [events, eventFilter, search]);

  const copy = (text) => {
    navigator.clipboard?.writeText(text);
    toast(T('msg.copied'));
  };

  const runRenewAll = () => {
    Modal.confirm({
      title: T('renew.title'),
      content: T('renew.body', { n: subCounts.due, month: monthKey }),
      okText: T('renew.ok'),
      cancelText: T('common.cancel'),
      onOk: async () => {
        setRenewing(true);
        try {
          const res = await apiClient.post('/api/consultation/admin/renew-subscriptions', { limit: 500 });
          setRenewResult(res.data);
          toast(T('renew.done', { renewed: res.data.renewed || 0, failed: res.data.failed || 0 }), res.data.failed ? 'warning' : 'success');
          await load();
        } catch (error) {
          toast(T('renew.failed', { error: errMsg(error) }), 'error');
        } finally {
          setRenewing(false);
        }
      },
    });
  };

  const retryUser = async (s) => {
    setBusyUser(s.userId);
    try {
      const res = await apiClient.post(`/api/consultation/admin/subscriptions/${encodeURIComponent(s.userId)}/retry`);
      const r = res.data || {};
      if (r.renewed) toast(T('msg.retryOk', { price: won(r.priceKrw) }));
      else toast(T('msg.retrySkipped', { reason: T(`reason.${r.reason || 'unknown'}`) }), 'info');
      await load();
    } catch (error) {
      const code = error?.response?.data?.code;
      toast(T('msg.retryFailed', { error: code ? T(`code.${code}`, { defaultValue: code }) : errMsg(error) }), 'error');
      await load();
    } finally {
      setBusyUser(null);
    }
  };

  const cancelUser = (s) => {
    Modal.confirm({
      title: T('cancel.title'),
      content: T('cancel.body', { user: s.email || s.userId, credits: s.packBalance }),
      okText: T('cancel.ok'),
      okButtonProps: { danger: true },
      cancelText: T('common.cancel'),
      onOk: async () => {
        setBusyUser(s.userId);
        try {
          await apiClient.post(`/api/consultation/admin/subscriptions/${encodeURIComponent(s.userId)}/cancel`, { reason: 'admin_monitor' });
          toast(T('msg.canceled'));
          await load();
        } catch (error) {
          toast(T('msg.actionFailed', { error: errMsg(error) }), 'error');
        } finally {
          setBusyUser(null);
        }
      },
    });
  };

  const openGrant = (target = '') => {
    setGrant({ target, packs: 1, reason: '' });
    setGrantOpen(true);
  };

  const submitGrant = async () => {
    const target = grant.target.trim();
    if (!target) return toast(T('grant.targetRequired'), 'error');
    if (!grant.reason.trim()) return toast(T('grant.reasonRequired'), 'error');
    setGranting(true);
    try {
      const body = target.includes('@') ? { email: target } : { userId: target };
      const res = await apiClient.post('/api/consultation/packs/grant', { ...body, packs: grant.packs, reason: grant.reason.trim() });
      toast(T('grant.done', { credits: res.data.credits || 0 }));
      setGrantOpen(false);
      await load();
    } catch (error) {
      toast(T('grant.failed', { error: errMsg(error) }), 'error');
    } finally {
      setGranting(false);
    }
  };

  const statusBadge = (s) => {
    if (!s.active) return <span className="rounded bg-slate-100 px-1.5 py-0.5 text-[11px] font-semibold text-slate-500">{T('status.canceled')}</span>;
    if (s.billingStatus === 'failed') return <span className="rounded bg-rose-50 px-1.5 py-0.5 text-[11px] font-semibold text-rose-700">{T('status.failed')}</span>;
    if (s.lastBilledMonthKey !== data?.monthKey) return <span className="rounded bg-amber-50 px-1.5 py-0.5 text-[11px] font-semibold text-amber-700">{T('status.due')}</span>;
    return <span className="rounded bg-emerald-50 px-1.5 py-0.5 text-[11px] font-semibold text-emerald-700">{T('status.ok')}</span>;
  };

  const userCell = (r) => (
    <div className="min-w-0">
      <div className="truncate text-xs font-semibold text-slate-800">{r.name || r.email || T('common.unknownUser')}</div>
      <div className="flex items-center gap-1 text-[11px] text-slate-400">
        <span className="truncate">{r.email || r.userId}</span>
        {r.userId ? (
          <button type="button" onClick={(e) => { e.stopPropagation(); copy(r.userId); }} className="shrink-0 hover:text-slate-600" title={T('action.copyUid')}>
            <Copy size={11} />
          </button>
        ) : null}
      </div>
    </div>
  );

  const subColumns = [
    { title: T('col.user'), key: 'user', render: (_, r) => userCell(r), width: 240 },
    { title: T('col.status'), key: 'status', render: (_, r) => (
      <div className="flex flex-wrap items-center gap-1">
        {statusBadge(r)}
        {r.issues?.length ? (
          <Tooltip title={r.issues.map((i) => T(`issue.${i}`)).join(' · ')}>
            <ShieldAlert size={14} className="text-rose-500" />
          </Tooltip>
        ) : null}
      </div>
    ), width: 120 },
    { title: T('col.plan'), key: 'plan', render: (_, r) => (
      <div className="text-xs">
        <div className="font-semibold text-slate-800">{T('plan.credits', { n: r.monthlyCredits })}</div>
        <div className="text-[11px] text-slate-400">{won(r.monthlyPriceKrw)} / {T('plan.month')}</div>
      </div>
    ), width: 120 },
    { title: T('col.lastBilled'), dataIndex: 'lastBilledMonthKey', render: (v) => <span className="text-xs tabular-nums text-slate-600">{v || '—'}</span>, width: 100 },
    { title: T('col.lastError'), key: 'err', render: (_, r) => r.lastBillingError ? (
      <div className="text-[11px]">
        <div className="font-semibold text-rose-600">{T(`code.${r.lastBillingError}`, { defaultValue: r.lastBillingError })}</div>
        <div className="text-slate-400">{fmtDate(r.lastBillingAttemptAt)}{r.failCount ? ` · ${T('plan.failCount', { n: r.failCount })}` : ''}</div>
      </div>
    ) : <span className="text-xs text-slate-300">—</span>, width: 170 },
    { title: T('col.balance'), key: 'bal', render: (_, r) => (
      <div className="text-[11px] tabular-nums text-slate-600">
        <div>{T('plan.packBalance', { n: r.packBalance })}</div>
        <div className="text-slate-400">{T('plan.freeUsed', { used: r.freeUsed, limit: r.freeLimit })}</div>
      </div>
    ), width: 120 },
    { title: T('col.started'), key: 'started', render: (_, r) => (
      <div className="text-[11px] text-slate-500">
        <div>{fmtDate(r.startedAt)}</div>
        {!r.active && r.canceledAt ? <div className="text-slate-400">{T('plan.canceledAt', { date: fmtDate(r.canceledAt), by: T(`by.${r.canceledBy || 'user'}`) })}</div> : null}
      </div>
    ), width: 140 },
    { title: '', key: 'actions', fixed: 'right', width: 120, render: (_, r) => (
      <div className="flex items-center justify-end gap-1" onClick={(e) => e.stopPropagation()}>
        {r.active && r.lastBilledMonthKey !== data?.monthKey ? (
          <Tooltip title={T('action.retry')}>
            <button type="button" disabled={busyUser === r.userId} onClick={() => retryUser(r)} className="rounded p-1.5 text-sky-600 hover:bg-sky-50 disabled:opacity-40">
              <RotateCcw size={14} className={busyUser === r.userId ? 'animate-spin' : ''} />
            </button>
          </Tooltip>
        ) : null}
        <Tooltip title={T('action.grant')}>
          <button type="button" onClick={() => openGrant(r.email || r.userId)} className="rounded p-1.5 text-violet-600 hover:bg-violet-50">
            <Gift size={14} />
          </button>
        </Tooltip>
        {r.active ? (
          <Tooltip title={T('action.cancel')}>
            <button type="button" disabled={busyUser === r.userId} onClick={() => cancelUser(r)} className="rounded p-1.5 text-rose-600 hover:bg-rose-50 disabled:opacity-40">
              <XCircle size={14} />
            </button>
          </Tooltip>
        ) : null}
      </div>
    ) },
  ];

  const eventColumns = [
    { title: T('col.time'), dataIndex: 'createdAt', render: (v) => <span className="whitespace-nowrap text-[11px] tabular-nums text-slate-500">{fmtDate(v)}</span>, width: 110 },
    { title: T('col.event'), dataIndex: 'type', render: (v) => (
      <span className={`whitespace-nowrap rounded px-1.5 py-0.5 text-[11px] font-semibold ${EVENT_TONE[v] || 'bg-slate-100 text-slate-600'}`}>{T(`event.${v}`, { defaultValue: v })}</span>
    ), width: 150 },
    { title: T('col.user'), key: 'user', render: (_, r) => userCell(r), width: 220 },
    { title: T('col.detail'), key: 'detail', render: (_, e) => {
      const parts = [];
      if (e.credits != null) parts.push(T('detail.credits', { n: e.credits }));
      if (e.priceKrw) parts.push(won(e.priceKrw));
      if (e.source) parts.push(T(`source.${e.source}`, { defaultValue: e.source }));
      if (e.fromFAQ) parts.push('FAQ');
      if (e.fromCache) parts.push(T('detail.cache'));
      if (e.code) parts.push(T(`code.${e.code}`, { defaultValue: e.code }));
      if (e.reason) parts.push(`“${e.reason}”`);
      if (e.adminUid) parts.push(T('detail.byAdmin'));
      return <span className="text-[11px] text-slate-600">{parts.join(' · ') || '—'}</span>;
    } },
    { title: T('col.month'), dataIndex: 'monthKey', render: (v) => <span className="text-[11px] tabular-nums text-slate-400">{v || '—'}</span>, width: 80 },
  ];

  const kpis = [
    { label: T('stat.usage'), value: Number(stats.usageCount || 0).toLocaleString(), hint: T('stat.usageHint', { free: stats.usageFree || 0, pack: stats.usagePack || 0, ai: stats.usageAi || 0, faq: stats.usageFaq || 0 }), Icon: Sparkles, tone: 'text-sky-600 bg-sky-50' },
    { label: T('stat.revenue'), value: won(stats.purchaseRevenueKrw), hint: T('stat.revenueHint', { sub: won(stats.subscriptionRevenueKrw), pack: won(stats.packRevenueKrw) }), Icon: CreditCard, tone: 'text-emerald-600 bg-emerald-50' },
    { label: T('stat.mrr'), value: won(data?.mrrKrw), hint: T('stat.mrrHint', { n: data?.activeSubscriptions || 0 }), Icon: TrendingUp, tone: 'text-indigo-600 bg-indigo-50' },
    { label: T('stat.billing'), value: `${stats.renewals || 0} / ${subCounts.due}`, hint: T('stat.billingHint', { failed: data?.billingFailedSubscriptions || 0, starts: stats.subscriptionStarts || 0, cancels: stats.subscriptionCancels || 0 }), Icon: Users, tone: subCounts.failed ? 'text-rose-600 bg-rose-50' : 'text-slate-600 bg-slate-100' },
    { label: T('stat.exceeded'), value: Number(stats.quotaExceededCount || 0).toLocaleString(), hint: T('stat.exceededHint', { refunds: stats.refundCount || 0, grants: stats.grantCount || 0 }), Icon: AlertTriangle, tone: 'text-amber-600 bg-amber-50' },
  ];

  const maxRevenue = Math.max(1, ...trend.map((m) => m.purchaseRevenueKrw || 0));
  const alerts = [];
  if (subCounts.failed) alerts.push({ tone: 'rose', text: T('alert.failed', { n: subCounts.failed }), filter: 'failed' });
  if (subCounts.due - subCounts.failed > 0) alerts.push({ tone: 'amber', text: T('alert.due', { n: subCounts.due - subCounts.failed, month: monthKey }), filter: 'due' });
  if (data?.issueCount) alerts.push({ tone: 'rose', text: T('alert.issues', { n: data.issueCount }), filter: 'issues' });
  if (stats.creditGrantFailures) alerts.push({ tone: 'rose', text: T('alert.creditGrant', { n: stats.creditGrantFailures }), filter: null, events: 'credit_grant_failed' });

  const subPills = ['active', 'failed', 'due', 'issues', 'canceled', 'all'];
  const tabs = [
    { key: 'subscribers', label: T('tab.subscribers'), Icon: Users, count: subscribers.length },
    { key: 'events', label: T('tab.events'), Icon: Activity, count: events.length },
    { key: 'trend', label: T('tab.trend'), Icon: TrendingUp },
  ];

  return (
    <div className="min-h-screen bg-slate-50 p-3">
      <div className="mb-3 rounded-lg border border-slate-200 bg-white">
        <div className="flex flex-wrap items-start justify-between gap-3 px-4 pt-3">
          <div>
            <h1 className="text-base font-bold text-slate-900">{T('title')}</h1>
            <div className="mt-1 flex flex-wrap gap-1.5 text-[11px]">
              <span className="rounded bg-slate-100 px-2 py-0.5 font-medium text-slate-600">{T('chip.month', { month: monthKey })}</span>
              <span className="rounded bg-sky-50 px-2 py-0.5 font-medium text-sky-700">{T('chip.free', { n: data?.freeLimit ?? 10 })}</span>
              <span className="rounded bg-emerald-50 px-2 py-0.5 font-medium text-emerald-700">{T('chip.pack', { n: data?.packSize ?? 10, price: won(data?.packPriceKrw) })}</span>
              <span className="rounded bg-slate-100 px-2 py-0.5 font-medium text-slate-500">{T('chip.retry', { h: Math.round((data?.retryIntervalMs || 0) / 3600000) || 6 })}</span>
            </div>
          </div>
          <div className="flex flex-wrap gap-1.5">
            <button type="button" onClick={() => openGrant()} className={`${btn} border border-violet-200 bg-violet-50 text-violet-700 hover:bg-violet-100`}>
              <Gift size={14} /> {T('action.grant')}
            </button>
            <button type="button" onClick={runRenewAll} disabled={renewing || loading} className={`${btn} border border-slate-300 bg-white text-slate-700 hover:bg-slate-100`}>
              <CreditCard size={14} /> {renewing ? T('renew.running') : T('renew.button', { n: subCounts.due })}
            </button>
            <button type="button" onClick={load} disabled={loading} className={`${btn} bg-slate-900 text-white hover:bg-slate-800`}>
              <RefreshCw size={14} className={loading ? 'animate-spin' : ''} /> {T('action.refresh')}
            </button>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-2 px-4 py-3 md:grid-cols-3 xl:grid-cols-5">
          {kpis.map((k) => (
            <div key={k.label} className="rounded-md border border-slate-200 px-3 py-2">
              <div className="flex items-center justify-between">
                <span className="text-[11px] font-medium text-slate-500">{k.label}</span>
                <span className={`rounded p-1 ${k.tone}`}><k.Icon size={13} /></span>
              </div>
              <div className="mt-0.5 text-lg font-bold tabular-nums text-slate-900">{loading && !data ? '…' : k.value}</div>
              <div className="truncate text-[11px] text-slate-400" title={k.hint}>{k.hint}</div>
            </div>
          ))}
        </div>
        <div className="flex gap-1 border-t border-slate-100 px-3">
          {tabs.map((tb) => (
            <button key={tb.key} type="button" onClick={() => setTab(tb.key)}
              className={`flex items-center gap-1.5 border-b-2 px-3 py-2 text-xs font-semibold ${tab === tb.key ? 'border-slate-900 text-slate-900' : 'border-transparent text-slate-500 hover:text-slate-700'}`}>
              <tb.Icon size={13} /> {tb.label}
              {tb.count != null ? <span className="rounded bg-slate-100 px-1.5 text-[10px] text-slate-500">{tb.count}</span> : null}
            </button>
          ))}
        </div>
      </div>

      {alerts.length ? (
        <div className="mb-3 space-y-1.5">
          {alerts.map((a) => (
            <button key={a.text} type="button"
              onClick={() => { if (a.filter) { setTab('subscribers'); setSubFilter(a.filter); } else if (a.events) { setTab('events'); setEventFilter(a.events); } }}
              className={`flex w-full items-center gap-2 rounded-md border px-3 py-2 text-left text-xs ${a.tone === 'rose' ? 'border-rose-200 bg-rose-50 text-rose-800' : 'border-amber-200 bg-amber-50 text-amber-800'}`}>
              <AlertTriangle size={14} className="shrink-0" /> {a.text}
            </button>
          ))}
        </div>
      ) : null}

      {renewResult ? (
        <div className="mb-3 rounded-md border border-slate-200 bg-white px-3 py-2 text-xs text-slate-600">
          <div className="flex items-center justify-between">
            <span className="font-semibold text-slate-800"><ListChecks size={13} className="mr-1 inline" />{T('renew.resultTitle', { month: renewResult.monthKey })}</span>
            <button type="button" className="text-slate-400 hover:text-slate-600" onClick={() => setRenewResult(null)}>✕</button>
          </div>
          <div className="mt-1 flex flex-wrap gap-3 tabular-nums">
            <span>{T('renew.scanned', { n: renewResult.scanned || 0 })}</span>
            <span className="text-emerald-700">{T('renew.renewed', { n: renewResult.renewed || 0 })}</span>
            <span className="text-rose-700">{T('renew.failedCount', { n: renewResult.failed || 0 })}</span>
            <span>{T('renew.skipped', { n: renewResult.skipped || 0 })}</span>
            {renewResult.remaining ? <span className="text-amber-700">{T('renew.remaining', { n: renewResult.remaining })}</span> : null}
          </div>
          {renewResult.failures?.length ? (
            <div className="mt-1 text-[11px] text-rose-600">
              {renewResult.failures.slice(0, 10).map((f) => `${f.userId.slice(0, 8)}…: ${T(`code.${f.code}`, { defaultValue: f.code })}`).join(' · ')}
            </div>
          ) : null}
        </div>
      ) : null}

      <div className="rounded-lg border border-slate-200 bg-white">
        {tab !== 'trend' ? (
          <div className="flex flex-wrap items-center gap-2 border-b border-slate-100 bg-slate-50 px-3 py-2">
            <Input size="small" allowClear prefix={<Search size={13} className="text-slate-400" />} value={search}
              onChange={(e) => setSearch(e.target.value)} placeholder={T('toolbar.search')} className="!w-64" />
            {tab === 'subscribers' ? (
              <div className="flex flex-wrap gap-1">
                {subPills.map((p) => (
                  <button key={p} type="button" onClick={() => setSubFilter(p)}
                    className={`rounded-full px-2.5 py-0.5 text-[11px] font-semibold ${subFilter === p ? 'bg-slate-900 text-white' : 'bg-white text-slate-600 ring-1 ring-slate-200 hover:bg-slate-100'}`}>
                    {T(`filter.${p}`)} <span className="opacity-70">{subCounts[p]}</span>
                  </button>
                ))}
              </div>
            ) : (
              <Select size="small" value={eventFilter} onChange={setEventFilter} className="!w-48"
                options={[{ value: 'all', label: T('filter.allEvents') }, { value: 'failures', label: T('filter.failures') },
                  ...EVENT_TYPES.map((v) => ({ value: v, label: T(`event.${v}`) }))]} />
            )}
          </div>
        ) : null}

        {tab === 'subscribers' ? (
          <Table size="small" rowKey="userId" loading={loading} dataSource={filteredSubs} columns={subColumns}
            scroll={{ x: 1100 }} pagination={{ pageSize: 20, size: 'small', showTotal: (n) => T('common.total', { n }) }}
            onRow={(r) => ({ onClick: () => setDetail(r), className: 'cursor-pointer' })}
            locale={{ emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={T('empty.subscribers')} /> }} />
        ) : null}

        {tab === 'events' ? (
          <Table size="small" rowKey="id" loading={loading} dataSource={filteredEvents} columns={eventColumns}
            scroll={{ x: 900 }} pagination={{ pageSize: 25, size: 'small', showTotal: (n) => T('common.total', { n }) }}
            locale={{ emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={T('empty.events')} /> }} />
        ) : null}

        {tab === 'trend' ? (
          <div className="p-3">
            <div className="mb-3 flex h-40 items-end gap-3 rounded-md border border-slate-100 px-3 pb-2 pt-4">
              {trend.map((m) => {
                const sub = m.subscriptionRevenueKrw || 0;
                const total = m.purchaseRevenueKrw || 0;
                const h = Math.round((total / maxRevenue) * 100);
                const subH = total ? Math.round((sub / total) * h) : 0;
                return (
                  <Tooltip key={m.monthKey} title={T('trend.tooltip', { total: won(total), sub: won(sub), pack: won(m.packRevenueKrw) })}>
                    <div className="flex h-full flex-1 flex-col items-center justify-end gap-1">
                      <span className="text-[10px] tabular-nums text-slate-500">{total ? `${Math.round(total / 1000).toLocaleString()}k` : ''}</span>
                      <div className="flex w-full max-w-[48px] flex-col justify-end overflow-hidden rounded-t bg-emerald-200" style={{ height: `${Math.max(h, total ? 3 : 0)}%` }}>
                        <div className="w-full bg-indigo-400" style={{ height: `${h ? (subH / h) * 100 : 0}%` }} />
                      </div>
                      <span className={`text-[10px] tabular-nums ${m.monthKey === monthKey ? 'font-bold text-slate-900' : 'text-slate-400'}`}>{m.monthKey.slice(2)}</span>
                    </div>
                  </Tooltip>
                );
              })}
            </div>
            <div className="mb-2 flex gap-3 text-[11px] text-slate-500">
              <span><span className="mr-1 inline-block h-2 w-2 rounded-sm bg-indigo-400" />{T('trend.subRevenue')}</span>
              <span><span className="mr-1 inline-block h-2 w-2 rounded-sm bg-emerald-200" />{T('trend.packRevenue')}</span>
            </div>
            <Table size="small" rowKey="monthKey" pagination={false} dataSource={[...trend].reverse()} scroll={{ x: 900 }}
              columns={[
                { title: T('col.month'), dataIndex: 'monthKey', render: (v) => <span className="text-xs font-semibold tabular-nums">{v}</span> },
                { title: T('trend.usage'), dataIndex: 'usageCount', render: (v, m) => <span className="text-xs tabular-nums">{v} <span className="text-slate-400">({m.usageFree}/{m.usagePack})</span></span> },
                { title: T('trend.exceeded'), dataIndex: 'quotaExceededCount', render: (v) => <span className="text-xs tabular-nums">{v}</span> },
                { title: T('trend.revenue'), dataIndex: 'purchaseRevenueKrw', render: (v) => <span className="text-xs font-semibold tabular-nums">{won(v)}</span> },
                { title: T('trend.subRevenue'), dataIndex: 'subscriptionRevenueKrw', render: (v) => <span className="text-xs tabular-nums">{won(v)}</span> },
                { title: T('trend.packRevenue'), dataIndex: 'packRevenueKrw', render: (v) => <span className="text-xs tabular-nums">{won(v)}</span> },
                { title: T('trend.subs'), key: 'subs', render: (_, m) => <span className="text-xs tabular-nums">+{m.subscriptionStarts} / −{m.subscriptionCancels}</span> },
                { title: T('trend.renewals'), key: 'ren', render: (_, m) => <span className="text-xs tabular-nums">{m.renewals} <span className="text-rose-500">{m.renewFailures ? `(${T('trend.failShort', { n: m.renewFailures })})` : ''}</span></span> },
                { title: T('trend.grants'), dataIndex: 'grantCount', render: (v) => <span className="text-xs tabular-nums">{v}</span> },
              ]} />
            <p className="mt-2 text-[11px] text-slate-400">{T('trend.note')}</p>
          </div>
        ) : null}
      </div>

      <Modal open={grantOpen} title={<span className="flex items-center gap-2"><Gift size={16} className="text-violet-600" />{T('grant.title')}</span>}
        onCancel={() => setGrantOpen(false)} onOk={submitGrant} confirmLoading={granting}
        okText={T('grant.ok')} cancelText={T('common.cancel')} destroyOnHidden>
        <div className="space-y-3">
          <label className="block text-xs font-medium text-slate-600">
            {T('grant.target')}
            <Input className="mt-1" value={grant.target} onChange={(e) => setGrant((g) => ({ ...g, target: e.target.value }))} placeholder={T('grant.targetPlaceholder')} />
          </label>
          <label className="block text-xs font-medium text-slate-600">
            {T('grant.packs', { size: data?.packSize ?? 10 })}
            <InputNumber className="mt-1 !w-full" min={1} max={100} value={grant.packs} onChange={(v) => setGrant((g) => ({ ...g, packs: v || 1 }))} />
          </label>
          <label className="block text-xs font-medium text-slate-600">
            {T('grant.reason')}
            <Input className="mt-1" value={grant.reason} maxLength={200} onChange={(e) => setGrant((g) => ({ ...g, reason: e.target.value }))} placeholder={T('grant.reasonPlaceholder')} />
          </label>
          <div className="rounded-md bg-violet-50 px-3 py-2 text-xs text-violet-800">
            {T('grant.preview', { credits: (grant.packs || 1) * (data?.packSize ?? 10), value: won((grant.packs || 1) * (data?.packPriceKrw || 0)) })}
          </div>
        </div>
      </Modal>

      <Drawer open={!!detail} onClose={() => setDetail(null)} width={420} title={detail ? (detail.name || detail.email || detail.userId) : ''}>
        {detail ? (
          <div className="space-y-3 text-xs">
            <div className="flex items-center gap-2">{statusBadge(detail)}<span className="text-slate-400">{detail.userId}</span></div>
            {detail.issues?.length ? (
              <div className="rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-rose-800">
                {detail.issues.map((i) => <div key={i}>• {T(`issue.${i}`)}</div>)}
              </div>
            ) : null}
            <dl className="grid grid-cols-2 gap-x-3 gap-y-2">
              {[
                [T('col.plan'), `${T('plan.credits', { n: detail.monthlyCredits })} · ${won(detail.monthlyPriceKrw)}`],
                [T('col.lastBilled'), detail.lastBilledMonthKey || '—'],
                [T('col.lastError'), detail.lastBillingError ? T(`code.${detail.lastBillingError}`, { defaultValue: detail.lastBillingError }) : '—'],
                [T('drawer.lastAttempt'), fmtDate(detail.lastBillingAttemptAt)],
                [T('drawer.failCount'), detail.failCount],
                [T('col.started'), fmtDate(detail.startedAt)],
                [T('drawer.canceledAt'), detail.canceledAt ? `${fmtDate(detail.canceledAt)} (${T(`by.${detail.canceledBy || 'user'}`)})` : '—'],
                [T('col.balance'), `${T('plan.packBalance', { n: detail.packBalance })} · ${T('plan.freeUsed', { used: detail.freeUsed, limit: detail.freeLimit })}`],
              ].map(([k, v]) => (
                <React.Fragment key={k}>
                  <dt className="text-slate-400">{k}</dt>
                  <dd className="font-medium text-slate-800">{v}</dd>
                </React.Fragment>
              ))}
            </dl>
            <div className="flex flex-wrap gap-1.5 border-t border-slate-100 pt-3">
              {detail.active && detail.lastBilledMonthKey !== data?.monthKey ? (
                <button type="button" onClick={() => retryUser(detail)} className={`${btn} bg-sky-600 text-white hover:bg-sky-700`}><RotateCcw size={13} /> {T('action.retry')}</button>
              ) : null}
              <button type="button" onClick={() => openGrant(detail.email || detail.userId)} className={`${btn} border border-violet-200 bg-violet-50 text-violet-700`}><Gift size={13} /> {T('action.grant')}</button>
              {detail.active ? (
                <button type="button" onClick={() => cancelUser(detail)} className={`${btn} border border-rose-200 bg-rose-50 text-rose-700`}><XCircle size={13} /> {T('action.cancel')}</button>
              ) : null}
            </div>
            <div className="rounded-md bg-slate-50 px-3 py-2 text-[11px] leading-relaxed text-slate-500">
              {detail.billingStatus === 'failed' ? <><Clock size={12} className="mr-1 inline" />{T('drawer.failedHint')}</> : <><CheckCircle2 size={12} className="mr-1 inline" />{T('drawer.okHint')}</>}
            </div>
          </div>
        ) : null}
      </Drawer>
    </div>
  );
};

export default ConsultationQuotaMonitorPage;
